"use es6";

import { __assign, __spreadArray } from "apollo-stack-hubspot/internal/tslib/tslib";
import * as React from "react";
import { invariant } from "apollo-stack-hubspot/@apollo/client/utilities/globals/index";
import { ApolloError, NetworkStatus } from "apollo-stack-hubspot/@apollo/client/core/index";
import { isNonEmptyArray } from "apollo-stack-hubspot/@apollo/client/utilities/index";
import { useApolloClient } from "apollo-stack-hubspot/@apollo/client/react/hooks/useApolloClient";
import { DocumentType, verifyDocumentType } from "apollo-stack-hubspot/@apollo/client/react/parser/index";
import { __use, useDeepMemo, wrapHook } from "apollo-stack-hubspot/@apollo/client/react/hooks/internal/index";
import { getSuspenseCache } from "apollo-stack-hubspot/@apollo/client/react/internal/index";
import { canonicalStringify } from "apollo-stack-hubspot/@apollo/client/cache/index";
import { skipToken } from "apollo-stack-hubspot/@apollo/client/react/hooks/constants";
export function useSuspenseQuery(query, options) {
  if (options === void 0) {
    options = Object.create(null);
  }
  return wrapHook("useSuspenseQuery", _useSuspenseQuery, useApolloClient(typeof options === "object" ? options.client : undefined))(query, options);
}
function _useSuspenseQuery(query, options) {
  var client = useApolloClient(options.client);
  var suspenseCache = getSuspenseCache(client);
  var watchQueryOptions = useWatchQueryOptions({
    client: client,
    query: query,
    options: options
  });
  var fetchPolicy = watchQueryOptions.fetchPolicy,
    variables = watchQueryOptions.variables;
  var _a = options.queryKey,
    queryKey = _a === void 0 ? [] : _a;
  var cacheKey = __spreadArray([query, canonicalStringify(variables)], [].concat(queryKey), true);
  var queryRef = suspenseCache.getQueryRef(cacheKey, function () {
    return client.watchQuery(watchQueryOptions);
  });
  var _b = React.useState([queryRef.key, queryRef.promise]),
    current = _b[0],
    setPromise = _b[1];
  // This saves us a re-execution of the render function when a variable changed.
  if (current[0] !== queryRef.key) {
    current[0] = queryRef.key;
    current[1] = queryRef.promise;
  }
  var promise = current[1];
  if (queryRef.didChangeOptions(watchQueryOptions)) {
    current[1] = promise = queryRef.applyOptions(watchQueryOptions);
  }
  React.useEffect(function () {
    var dispose = queryRef.retain();
    var removeListener = queryRef.listen(function (promise) {
      setPromise([queryRef.key, promise]);
    });
    return function () {
      removeListener();
      dispose();
    };
  }, [queryRef]);
  var skipResult = React.useMemo(function () {
    var error = toApolloError(queryRef.result);
    return {
      loading: false,
      data: queryRef.result.data,
      networkStatus: error ? NetworkStatus.error : NetworkStatus.ready,
      error: error
    };
  }, [queryRef.result]);
  var result = fetchPolicy === "standby" ? skipResult : __use(promise);
  var fetchMore = React.useCallback(function (options) {
    var promise = queryRef.fetchMore(options);
    setPromise([queryRef.key, queryRef.promise]);
    return promise;
  }, [queryRef]);
  var refetch = React.useCallback(function (variables) {
    var promise = queryRef.refetch(variables);
    setPromise([queryRef.key, queryRef.promise]);
    return promise;
  }, [queryRef]);
  var subscribeToMore = queryRef.observable.subscribeToMore;
  return React.useMemo(function () {
    return {
      client: client,
      data: result.data,
      error: toApolloError(result),
      networkStatus: result.networkStatus,
      fetchMore: fetchMore,
      refetch: refetch,
      subscribeToMore: subscribeToMore
    };
  }, [client, fetchMore, refetch, result, subscribeToMore]);
}
function validateOptions(options) {
  var query = options.query,
    fetchPolicy = options.fetchPolicy,
    returnPartialData = options.returnPartialData;
  verifyDocumentType(query, DocumentType.Query);
  validateFetchPolicy(fetchPolicy);
  validatePartialDataReturn(fetchPolicy, returnPartialData);
}
function validateFetchPolicy(fetchPolicy) {
  if (fetchPolicy === void 0) {
    fetchPolicy = "cache-first";
  }
  var supportedFetchPolicies = ["cache-first", "network-only", "no-cache", "cache-and-network"];
  invariant(supportedFetchPolicies.includes(fetchPolicy), 58, fetchPolicy);
}
function validatePartialDataReturn(fetchPolicy, returnPartialData) {
  if (fetchPolicy === "no-cache" && returnPartialData) {
    process.env.NODE_ENV !== "production" && invariant.warn(59);
  }
}
export function toApolloError(result) {
  return isNonEmptyArray(result.errors) ? new ApolloError({
    graphQLErrors: result.errors
  }) : result.error;
}
export function useWatchQueryOptions(_a) {
  var client = _a.client,
    query = _a.query,
    options = _a.options;
  return useDeepMemo(function () {
    var _a;
    if (options === skipToken) {
      return {
        query: query,
        fetchPolicy: "standby"
      };
    }
    var fetchPolicy = options.fetchPolicy || ((_a = client.defaultOptions.watchQuery) === null || _a === void 0 ? void 0 : _a.fetchPolicy) || "cache-first";
    var watchQueryOptions = __assign(__assign({}, options), {
      fetchPolicy: fetchPolicy,
      query: query,
      notifyOnNetworkStatusChange: false,
      nextFetchPolicy: void 0
    });
    if (process.env.NODE_ENV !== "production") {
      validateOptions(watchQueryOptions);
    }
    // Assign the updated fetch policy after our validation since `standby` is
    // not a supported fetch policy on its own without the use of `skip`.
    if (options.skip) {
      watchQueryOptions.fetchPolicy = "standby";
    }
    return watchQueryOptions;
  }, [client, options, query]);
}